/* eslint-disable no-useless-escape */
/*
 * This component renders a subset of Markdown that we use in CS challenge
 * descriptions. The implementation is ripped straight from handlebars-extras.js
 * but we are not interested in depending on that anymore.
 *
 * NOTE: if you're coming from the future and wondering whether you can use this
 * to render markdown elsewhere on webapp, the answer is probably not! This
 * doesn't render a lot of fun markdown things like headers and tables.
 */

import {escapeEntities} from "@khan/util-escape-entities";

/* This is the thing we stole */
export const formatContent = (contentInput?: string | null): string => {
    // Escape content to sanitize.
    let content = escapeEntities(contentInput);

    const newlineRegex = /[\n]/g;
    content = content.replace(newlineRegex, "<br>");

    // Replace Markdown with HTML tags. (Keep in sync with edit function in
    // discussion.js.)
    content = content.replace(
        /(\W|^)_(\S.*?\S)_(\W|$)/g,
        function (m, a, b, c) {
            return a + "<em>" + b + "</em>" + c;
        },
    );

    content = content.replace(
        /(\W|^)\*(\b.*?\b)\*(\W|$)/g,
        function (m, a, b, c) {
            return a + "<b>" + b + "</b>" + c;
        },
    );

    // Difference between <code> and <pre><code>:
    // http://stackoverflow.com/q/4611591/

    // Replace code blocks demarcated with `` or equivalent HTML entities.
    [/&#x60;&#x60;&#x60;(.*?)&#x60;&#x60;&#x60;/gm, /```(.*?)```/gm].forEach(
        (regex) => {
            content = content.replace(regex, function (m, n) {
                // Remove leading line breaks.
                n = n.replace(/^\s*(<br>)+/, "");
                // Remove trailing line breaks.
                n = n.replace(/(<br>)+\s*$/, "");
                return "<pre><code>" + n + "</code></pre>";
            });
        },
    );

    // Replace inline code demarcated with `` or equivalent HTML entities.
    [/&#x60;(.*?)&#x60;/g, /`(.*?)`/g].forEach((regex) => {
        content = content.replace(regex, function (m, n) {
            return "<code>" + n + "</code>";
        });
    });

    content = Autolink.autolink(content, true);

    // Use SafeString because we already escaped the user generated
    // content and then added our own safe html
    return content;
};

/* We stole this, too, sorry. */
const Autolink = (function () {
    // from http://daringfireball.net/2010/07/improved_regex_for_matching_urls
    const regex =
        /\b(?:(?:https?:\/\/|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>&]+|&amp;|\((?:[^\s()<>]|(?:\([^\s()<>]+\)))*\))+(?:\((?:[^\s()<>]|(?:\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’&]))/gi;

    return {
        autolink: function (text: string, nofollow: boolean) {
            return text.replace(regex, function (url) {
                if (!/^https?:\/\//.test(url)) {
                    url = "http://" + url;
                }

                const nofollowAttr = nofollow ? 'rel="nofollow"' : "";

                return (
                    "<a " + nofollowAttr + ' href="' + url + '">' + url + "</a>"
                );
            });
        },
    };
})();

/* Strips leading spaces from a piece of code so the outermost indentation level
has no leading spaces. We actually did not steal this. */
export const stripExtraSpacesFromPre = (html: string): string => {
    const lines = html.split("\n");
    // We only want to do this to <pre> elements
    // and we'll ignore anything with less than three lines
    if (lines.length < 3 || lines[0].indexOf("<pre") === -1) {
        return html;
    }
    // We only want to look at the inner HTML
    const innerLines = lines.slice(1, lines.length - 1);
    // Find the leading spaces common to all lines
    const leadingCharPos = innerLines
        // We only want to consider lines with something other than empty space
        .filter((line) => line.trim().length)
        // Find the first non-space character or EOL on each line
        .map((line) => line.search(/\S|$/));
    // Find the smallest of these
    const minLineSpace =
        leadingCharPos.length > 0 ? Math.min(...leadingCharPos) : 0;
    // Plug everything back together
    return [
        lines[0],
        innerLines.map((line) => line.slice(minLineSpace)).join("\n"),
        lines[lines.length - 1],
    ]
        .map((str) => str.trim())
        .join("\n");
};
